import * as React from 'react'

const STORAGE_KEY = 'sistemas:sidebar:collapsed'

/** Recolhida por padrão, como no SYS — vale para o primeiro acesso. */
const DEFAULT_COLLAPSED = true

function read(): boolean {
  // SSR e navegador sem storage (modo privado restrito) caem no padrão.
  if (typeof window === 'undefined') return DEFAULT_COLLAPSED
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'true') return true
    if (stored === 'false') return false
    return DEFAULT_COLLAPSED
  } catch {
    return DEFAULT_COLLAPSED
  }
}

/**
 * Estado recolhido/expandido da sidebar, preservado entre recarregamentos.
 *
 * A escolha é preferência de trabalho, não estado de tela: quem expandiu para
 * ler os rótulos não quer que o F5 desfaça isso.
 *
 * Fica em `localStorage` e não no perfil do usuário de propósito — é preferência
 * por dispositivo. A mesma pessoa pode querer expandida no monitor grande e
 * recolhida no notebook.
 */
export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = React.useState<boolean>(read)

  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed))
    } catch {
      // Storage indisponível: a preferência vale só para esta sessão.
    }
  }, [collapsed])

  // Outra aba mudou a preferência — refletir aqui também.
  React.useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key !== STORAGE_KEY || event.newValue === null) return
      setCollapsed(event.newValue === 'true')
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const toggle = React.useCallback(() => setCollapsed((value) => !value), [])

  return [collapsed, toggle]
}

export const SIDEBAR_STORAGE_KEY = STORAGE_KEY
